<script setup lang="ts">
import Switch from "@/ui/components/switch/switch.vue";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import OffsetChartComponent from "@/modules/booking_experts/pages/Components/Chart/OffsetChartComponent.vue";

const {
	isLoading,
	showBorder = true,
	curveData,
	curveCaption,
	errorMessage,
} = defineProps<{
	isLoading: boolean;
	showBorder?: boolean;
	curveData?: {
		id: string;
		name: string;
	};
	curveCaption?: string;
	errorMessage?: string;
}>();

const store = useCurveModelStore();
</script>

<template>
	<v-chip
		v-if="!isLoading && curveData?.id !== undefined"
		rounded="sm"
		size="small"
		variant="tonal"
		class="ml-3 mt-3"
		style="height: 24px"
	>
		<span> {{ curveData.name }} </span>
		<v-divider vertical class="mx-1 flex-grow-1"></v-divider>
		<a
			:href="`/pricing/curves/${curveData.id}`"
			target="_blank"
			class="v-btn v-btn--variant-text v-btn--density-default v-btn--size-x-small d-flex align-center pa-0 text-decoration-none"
			v-tooltip="'View Pricing Model in new tab'"
		>
			<span class="mr-1"><b>Edit</b></span>
			<v-icon icon="mdi-open-in-new" />
		</a>
	</v-chip>
	<v-skeleton-loader
		v-else-if="isLoading"
		type="text"
		height="24"
		width="150"
	/>

	<div class="chart-container">
		<OffsetChartComponent
			v-if="!isLoading && curveData?.id !== undefined"
			is-readonly
			:show-footer="false"
			:show-header="false"
			:show-border="showBorder"
		/>
		<div v-else-if="!isLoading" class="chart-skeleton-container ma-2">
			<p class="text-medium-emphasis mt-2">
				{{
					(errorMessage ?? "").includes("No query results for model")
						? "No applied curve for this booking"
						: "No applied curve at the time of booking"
				}}
			</p>
		</div>
		<div v-else class="chart-skeleton-container ma-2">
			<v-progress-circular color="primary" indeterminate />
			<p class="text-caption text-medium-emphasis mt-2">Fetching data...</p>
		</div>
	</div>

	<v-divider class="mx-3" />

	<div class="curve-info-footer pa-3">
		<div>
			<v-chip
				size="small"
				color="primary"
				variant="flat"
				class="mr-1"
				prepend-icon="fa-solid fa-info-circle"
			>
				Curve Info
			</v-chip>
			<span class="text-caption text-medium-emphasis">
				{{ curveCaption ?? "This data is the currently applied price data." }}
			</span>
		</div>
		<v-spacer />
		<Switch :disabled="isLoading" v-model="store.showPY" label="PY" />
		<v-divider vertical class="mx-2" />
		<Switch :disabled="isLoading" v-model="store.showCY" label="CY" />
	</div>
</template>

<style scoped>
.chart-skeleton-container {
	height: 284px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	border-radius: 4px;
}

.curve-info-footer {
	display: flex;
	align-items: center;
}

.chart-container {
	position: relative;
	min-height: 280px;
}
</style>
